import { MediaAttachmentEntity, UploadState } from "@/indexedDB/types";
import { isMediaType } from "@/utils/attachmentType";
import { create } from "zustand";

type AttachmentViewerState = {
  open: boolean;
  attachment: MediaAttachmentEntity;
  /** Upload state for the currently viewed attachment, if it was opened while uploading */
  uploadState?: UploadState;
  /** Plain-text file size in bytes, required by the SW stream handler for chunked uploads */
  fileSize?: number;
  /**
   * Context the viewer was opened in — the parent message id when opened from
   * inside a thread, or null for the main chat. Scopes the media navigation
   * list so thread media and main-chat media never bleed into each other.
   */
  threadId: string | null;
  index: number;
  attachments: MediaAttachmentEntity[];
  showNext: boolean;
  showPrevious: boolean;
};

type OpenDialogOptions = {
  /** Parent message id when opening from a thread; null/omitted for the main chat. */
  threadId?: string | null;
  uploadState?: UploadState;
  fileSize?: number;
};

type Actions = {
  closeDialog: () => void;
  openDialog: (
    attachment: MediaAttachmentEntity,
    options?: OpenDialogOptions,
  ) => void;
  setAttachments: (attachments: MediaAttachmentEntity[]) => void;
  nextAttachment: () => void;
  previousAttachment: () => void;
};

const initialState: AttachmentViewerState = {
  open: false,
  index: -1,
  attachment: {} as MediaAttachmentEntity,
  attachments: [],
  showNext: false,
  showPrevious: false,
  threadId: null,
};

export const useAttachmentViewerState = create<
  AttachmentViewerState & Actions
>()((set) => ({
  // State
  ...initialState,

  // Actions
  closeDialog() {
    set({ ...initialState });
  },
  openDialog(
    attachment: MediaAttachmentEntity,
    options: OpenDialogOptions = {},
  ) {
    set({
      open: true,
      attachment,
      threadId: options.threadId ?? null,
      uploadState: options.uploadState,
      fileSize: options.fileSize,
    });
  },
  setAttachments(attachments: MediaAttachmentEntity[]) {
    set((state) => {
      // Documents (PDF, "content not supported") are a single-document view BY
      // DESIGN — they never join the media carousel. Without this gate their
      // findIndex is -1, which made `showNext` true (-1 < length-1) whenever
      // the chat had any media: a Next arrow / ArrowRight / swipe on a PDF
      // jumped to the chat's oldest image.
      if (!isMediaType(state.attachment.fileType)) {
        return {
          index: -1,
          attachments: [],
          showNext: false,
          showPrevious: false,
        };
      }
      const currentIndex = attachments.findIndex(
        (att) => att.id === state.attachment.id,
      );
      // `currentIndex >= 0`: a media item momentarily absent from the list
      // (e.g. its message was just deleted; the close-on-delete hook races this
      // update) must not enable "next" onto an unrelated index.
      return {
        attachments,
        ...positionFor(currentIndex, attachments.length),
      };
    });
  },
  nextAttachment() {
    set((state) => slideTo(state, state.index + 1));
  },
  previousAttachment() {
    set((state) => slideTo(state, state.index - 1));
  },
}));

// Index + arrow-visibility for a position in the list. `index < 0` (the current
// item isn't in the list) keeps both arrows off.
function positionFor(index: number, length: number) {
  return {
    index,
    showNext: index >= 0 && index < length - 1,
    showPrevious: index > 0,
  };
}

// Move to a clamped target index and adopt that entity's stream metadata.
// fileSize/uploadState are DERIVED from the target (not cleared): a chunked
// video reached by arrow/swipe needs them for the SW stream registration
// (useVideoStream), exactly like a direct open.
function slideTo(
  state: AttachmentViewerState,
  targetIndex: number,
): Partial<AttachmentViewerState> {
  const { attachments } = state;
  if (attachments.length === 0) {
    return state;
  }
  const index = Math.max(0, Math.min(targetIndex, attachments.length - 1));
  const attachment = attachments[index];
  return {
    attachment,
    uploadState: attachment.uploadState,
    fileSize: attachment.fileSize,
    ...positionFor(index, attachments.length),
  };
}
